import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { Layout, SectionContent, SectionHead } from "@layouts/MainLayout";
import { useTranslation } from "react-i18next";
import TableList from "@components/TableList";
import { RowSelectionState } from "@tanstack/react-table";
import { columns } from "@pages/user/drivers/_components/columns";
import { DeleteDriverResponse, Driver, DriversList } from "@lib/types/driver";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getDrivers } from "@lib/api/queries/drivers";
import { Plus } from "lucide-react";
import CreateDriver from "@pages/user/drivers/_components/createDriver";
import UpdateDriverForm from "./_components/updateDriver";
import { deleteDriver } from "@lib/api/mutations/driver";
import { AxiosError } from "axios";
import useHandleError from "@lib/utils/errorHook";
import { useSnackbar } from "@context/SnackBarProvider";
import PromptDialog from "@components/PromptDialog";
import ViewDriver from "@pages/user/drivers/_components/viewDriver";
import CreateBulkDriver from "@pages/user/drivers/_components/createBulkDriver";
import SyncDriver from "@pages/user/drivers/_components/syncDriver";

const DriversMain: React.FC = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [addDriverDialogOpen, setAddDriverDialogOpen] = useState(false);
  const [syncDriverDialogOpen, setSyncDriverDialogOpen] = useState(false);
  const [bulkDriverDialogOpen, setBulkDriverDialogOpen] = useState(false);
  const [updateDriverDialogOpen, setUpdateDriverDialogOpen] = useState(false);
  const [driverToEdit, setDriverToEdit] = useState<Driver | undefined>(
    undefined
  );
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>(
    undefined
  );
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const {
    data: drivers,
    isFetching,
    refetch,
    error,
  } = useQuery<DriversList>({
    queryKey: ["drivers"],
    queryFn: () => getDrivers(),
    refetchOnWindowFocus: false,
  });

  const { mutate: deleteDriverfn, isPending: isDeletingDriver } = useMutation({
    mutationFn: deleteDriver,
    onSuccess: (data: DeleteDriverResponse) => {
      showSnackbar(t("user.drivers.deleteSuccess"), "success");

      setSelectedDriver(undefined);
      refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const confirmDeleteDriver = () => {
    if (!selectedDriver) return;
    deleteDriverfn(selectedDriver.id);
    setShowDeletePrompt(false);
  };

  const handleDeleteDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setShowDeletePrompt(true);
  };

  const handleViewDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setViewDialogOpen(true);
  };

  useEffect(() => {
    if (error instanceof AxiosError && error.response) {
      const errorDetails = handleError(error.response.data);
      errorDetails && showSnackbar(errorDetails.message, "error");
    }
  }, [error]);

  return (
    <Layout>
      <SectionHead title={t("user.drivers.header")}>
        <div className="flex items-center gap-2">
          <Button
            variant="contained"
            onClick={() => {
              setAddDriverDialogOpen(true);
            }}
            color="primary"
            className="flex items-center gap-1 p-2"
          >
            <Plus size={18} />
            <Typography variant="body2">{t("user.drivers.add")}</Typography>
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setBulkDriverDialogOpen(true);
            }}
            color="primary"
            className="flex items-center gap-1 p-2"
          >
            <Plus size={18} />
            <Typography variant="body2">{t("user.drivers.addBulk")}</Typography>
          </Button>
        </div>
      </SectionHead>

      <SectionContent>
        {!error && (
          <div className="flex items-center justify-between">
            <TableList
              data={drivers?.data || []}
              columns={columns(
                setDriverToEdit,
                setUpdateDriverDialogOpen,
                handleDeleteDriver,
                handleViewDriver,
                setSyncDriverDialogOpen
              )}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              isLoading={isFetching}
            />
          </div>
        )}

        <CreateDriver
          addDriverDialogOpen={addDriverDialogOpen}
          setAddDriverDialogOpen={setAddDriverDialogOpen}
          refetch={refetch}
        />

        <SyncDriver
          syncDriverDialogOpen={syncDriverDialogOpen}
          setSyncDriverDialogOpen={setSyncDriverDialogOpen}
          refetch={refetch}
          driverData={driverToEdit}
        />

        <CreateBulkDriver
          uploadDriverDialogOpen={bulkDriverDialogOpen}
          setUploadDriversDialogOpen={setBulkDriverDialogOpen}
          refetch={refetch}
        />

        <UpdateDriverForm
          driverData={driverToEdit}
          updateDriverDialogOpen={updateDriverDialogOpen}
          setUpdateDriverDialogOpen={setUpdateDriverDialogOpen}
          refetch={refetch}
        />

        <PromptDialog
          handleClose={() => setShowDeletePrompt(false)}
          handleConfirm={() => confirmDeleteDriver()}
          isOpen={showDeletePrompt}
          title={t("user.drivers.delete")}
          description={t("user.drivers.deletePrompt", {
            name: selectedDriver?.name,
          })}
        />

        <ViewDriver
          updateDriverDialogOpen={viewDialogOpen}
          setUpdateDriverDialogOpen={setViewDialogOpen}
          driverData={selectedDriver}
        />
      </SectionContent>
    </Layout>
  );
};

export default DriversMain;
